/**
 * 센터 수업(스케줄) 조회 React Query 훅 (Step 10)
 *
 * 기존 admin용 서비스 `fetchAdminStudiomateSchedules`(→ `getStudiomateSchedules` CF)를
 * 감싸 센터 상세 페이지에서 수업 목록을 조회한다. 백엔드의 `isConnected` 게이트가
 * 제거되어(Step 10), studiomate 연동 여부와 무관하게 `schedules` 미러를 읽는다.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { getFirebaseFunctions } from "@/config/firebase";
import { fetchAdminStudiomateSchedules } from "@/services/admin-studiomate-schedules";

/** center-schedules query key 빌더 (페이지의 invalidate와 공유) */
export function centerSchedulesQueryKey(centerId: number, startDate: string, endDate: string) {
  return ["center-schedules", centerId, startDate, endDate] as const;
}

/**
 * 특정 센터의 기간 내 수업 목록을 조회한다.
 *
 * @param centerId - 센터 ID
 * @param range - 조회 기간 (KST, YYYY-MM-DD)
 */
export function useCenterSchedules(
  centerId: number,
  range: { startDate: string; endDate: string },
) {
  const { startDate, endDate } = range;

  return useQuery({
    queryKey: centerSchedulesQueryKey(centerId, startDate, endDate),
    queryFn: () => fetchAdminStudiomateSchedules(centerId, startDate, endDate),
    // centerId가 유효하고 기간이 채워졌을 때만 조회
    enabled: Number.isFinite(centerId) && centerId > 0 && !!startDate && !!endDate,
    staleTime: 1000 * 60 * 5, // 5분
  });
}

// --- 수정/예약취소/삭제 contract (update-schedule.ts / cancel-booking.ts / delete-schedule.ts) ---

interface ScheduleMutationResponse {
  success: true;
}

export interface UpdateScheduleParams {
  centerId: number;
  scheduleId: number;
  title?: string;
  /** KST "YYYY-MM-DDTHH:mm" */
  startOn?: string;
  endOn?: string;
  staffName?: string;
  staffMobile?: string;
  maxTrainee?: number;
}

export interface CancelBookingParams {
  centerId: number;
  scheduleId: number;
  bookingId: number;
}

export interface DeleteScheduleParams {
  centerId: number;
  scheduleId: number;
}

/** 수업 정보 수정(reschedule). bookings는 변경하지 않는다. */
export function useUpdateSchedule() {
  return useMutation({
    mutationFn: async (params: UpdateScheduleParams) => {
      const fn = httpsCallable<UpdateScheduleParams, ScheduleMutationResponse>(
        getFirebaseFunctions(),
        "updateSchedule",
      );
      return (await fn(params)).data;
    },
  });
}

/** 예약 취소(bookingId 지정, status:cancel). */
export function useCancelBooking() {
  return useMutation({
    mutationFn: async (params: CancelBookingParams) => {
      const fn = httpsCallable<CancelBookingParams, ScheduleMutationResponse>(
        getFirebaseFunctions(),
        "cancelBooking",
      );
      return (await fn(params)).data;
    },
  });
}

/** 수업 삭제(소프트, isRemovedFromStudiomate:true). */
export function useDeleteSchedule() {
  return useMutation({
    mutationFn: async (params: DeleteScheduleParams) => {
      const fn = httpsCallable<DeleteScheduleParams, ScheduleMutationResponse>(
        getFirebaseFunctions(),
        "deleteSchedule",
      );
      return (await fn(params)).data;
    },
  });
}
